import React, { useMemo, useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import { Link } from "react-router-dom";
import {
  GET_MY_DRAFT_GISTS,
  PUBLISH_GIST,
} from "../../services/graphql/queriesMutations";
import { buildGistUrl, buildWorkspaceGistUrl } from "../../utils/publicUrls";
import styles from "./DraftsCenterPage.module.css";

type DraftSubject = {
  id: number;
  publicId: string;
  title: string;
  slug?: string | null;
};

type DraftEdit = {
  id: number;
  body: string;
  createdAt?: string | null;
  updatedAt?: string | null;
};

type DraftVersion = {
  id: number;
  point: string;
  createdAt?: string | null;
  edits?: DraftEdit[] | null;
};

type DraftGist = {
  id: number;
  publicId: string;
  title: string;
  slug?: string | null;
  subjectId: number;
  parentId?: number | null;
  status: "DRAFT" | "PUBLISHED";
  publishedAt?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
  canPublish: boolean;
  canEditInPlace: boolean;
  lockReasons: string[];
  subject: DraftSubject;
  versions?: DraftVersion[] | null;
};

type DraftsQueryData = {
  myDraftGists: DraftGist[];
};

const formatDate = (value?: string | null): string => {
  if (!value) return "unknown date";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "unknown date";

  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
};

const stripHtml = (value: string): string =>
  value
    .replace(/<script[\s\S]*?<\/script>/gi, " ")
    .replace(/<style[\s\S]*?<\/style>/gi, " ")
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\s+/g, " ")
    .trim();

const getLatestVersion = (gist: DraftGist): DraftVersion | null => {
  const versions = gist.versions ?? [];
  if (versions.length === 0) return null;

  return versions
    .slice()
    .sort((a, b) => Number(b.id) - Number(a.id))[0] ?? null;
};

const getLatestEdit = (gist: DraftGist): DraftEdit | null => {
  const latestVersion = getLatestVersion(gist);
  const edits = latestVersion?.edits ?? [];
  if (edits.length === 0) return null;

  return edits
    .slice()
    .sort((a, b) => Number(b.id) - Number(a.id))[0] ?? null;
};

const getPreview = (gist: DraftGist): string => {
  const latestVersion = getLatestVersion(gist);
  const latestEdit = getLatestEdit(gist);
  const text = stripHtml(latestEdit?.body || latestVersion?.point || "");

  if (!text) return "No body text yet. Open the draft in the workspace and continue writing.";
  if (text.length <= 260) return text;
  return `${text.slice(0, 260).trim()}…`;
};

const buildWorkspaceUrl = (gist: DraftGist, mode: "read" | "advanced" = "advanced"): string =>
  buildWorkspaceGistUrl(gist, mode);

const buildDraftTreeUrl = (gist: DraftGist): string =>
  `/workspace/draft-tree?gistPublicId=${encodeURIComponent(gist.publicId)}`;

const DraftsCenterPage: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [copiedDraftId, setCopiedDraftId] = useState<number | null>(null);
  const [publishingDraftId, setPublishingDraftId] = useState<number | null>(null);
  const [publishErrorByDraftId, setPublishErrorByDraftId] = useState<Record<number, string>>({});

  const { data, loading, error, refetch } = useQuery<DraftsQueryData>(GET_MY_DRAFT_GISTS, {
    fetchPolicy: "cache-and-network",
  });

  const [publishGist] = useMutation(PUBLISH_GIST);

  const drafts = useMemo(() => data?.myDraftGists ?? [], [data?.myDraftGists]);
  const normalizedSearch = searchTerm.trim().toLowerCase();

  const filteredDrafts = useMemo(() => {
    if (!normalizedSearch) return drafts;

    return drafts.filter((draft) => {
      const haystack = [
        draft.title,
        draft.publicId,
        draft.subject?.title,
        draft.subject?.publicId,
        getPreview(draft),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return haystack.includes(normalizedSearch);
    });
  }, [drafts, normalizedSearch]);

  const handleCopyWorkspaceLink = async (draft: DraftGist) => {
    const link = `${window.location.origin}${buildWorkspaceUrl(draft)}`;

    try {
      await window.navigator.clipboard.writeText(link);
      setCopiedDraftId(draft.id);
      window.setTimeout(() => setCopiedDraftId(null), 2200);
    } catch {
      setCopiedDraftId(null);
      window.prompt("Copy this workspace link:", link);
    }
  };

  const handlePublish = async (draft: DraftGist) => {
    setPublishingDraftId(draft.id);
    setPublishErrorByDraftId((current) => ({ ...current, [draft.id]: "" }));

    try {
      await publishGist({ variables: { id: draft.id } });
      await refetch();
    } catch (publishError) {
      setPublishErrorByDraftId((current) => ({
        ...current,
        [draft.id]: publishError instanceof Error ? publishError.message : "Could not publish this draft.",
      }));
    } finally {
      setPublishingDraftId(null);
    }
  };

  if (loading && drafts.length === 0) {
    return (
      <main className={styles.draftsPage}>
        <section className={styles.draftsShell}>
          <div className={styles.loadingState}>
            <p>Loading your drafts…</p>
          </div>
        </section>
      </main>
    );
  }

  if (error) {
    return (
      <main className={styles.draftsPage}>
        <section className={styles.draftsShell}>
          <div className={styles.errorState}>
            <h2>Drafts could not be loaded</h2>
            <p>{error.message}</p>
          </div>
        </section>
      </main>
    );
  }

  return (
    <main className={styles.draftsPage}>
      <section className={styles.draftsShell}>
        <header className={styles.draftsHero}>
          <div className={styles.draftsHeroTop}>
            <div>
              <p className={styles.draftsEyebrow}>Author workspace</p>
              <h1>My Drafts</h1>
            </div>
            <div className={styles.heroActionGroup}>
              <Link className={styles.heroActionLink} to="/workspace/draft-tree">
                Draft Trees
              </Link>
              <Link className={styles.heroActionLink} to="/workspace/bulk-authoring">
                Bulk Authoring
              </Link>
            </div>
          </div>
          <p className={styles.draftsHeroText}>
            This is the private holding area for Gists you saved as drafts. Drafts are not public pages,
            do not belong in the sitemap, and should be continued from the workspace until you publish them.
          </p>
          <div className={styles.draftsSummaryRow} aria-label="Draft summary">
            <span className={styles.summaryPill}>{drafts.length} draft{drafts.length === 1 ? "" : "s"}</span>
            <span className={styles.summaryPill}>Newest updates first</span>
            <span className={styles.summaryPill}>Private until published</span>
          </div>
        </header>

        <div className={styles.draftsToolbar}>
          <input
            className={styles.searchBox}
            type="search"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            placeholder="Search title, subject, or public code…"
            aria-label="Search drafts"
          />
          <p className={styles.toolbarHint}>
            Showing {filteredDrafts.length} of {drafts.length}
          </p>
        </div>

        {drafts.length === 0 ? (
          <div className={styles.emptyState}>
            <h2>No drafts yet</h2>
            <p>
              When you create a Gist and choose to save it as a private draft, it will appear here.
            </p>
          </div>
        ) : filteredDrafts.length === 0 ? (
          <div className={styles.emptyState}>
            <h2>No matching drafts</h2>
            <p>Try a different title, subject, or public code.</p>
          </div>
        ) : (
          <div className={styles.draftGrid}>
            {filteredDrafts.map((draft) => {
              const workspaceUrl = buildWorkspaceUrl(draft, "advanced");
              const readWorkspaceUrl = buildWorkspaceUrl(draft, "read");
              const futurePublicUrl = buildGistUrl(draft.subject, draft);
              const publishError = publishErrorByDraftId[draft.id];

              return (
                <article key={draft.id} className={styles.draftCard}>
                  <div className={styles.draftCardHeader}>
                    <div className={styles.draftTitleGroup}>
                      <h2 className={styles.draftTitle}>{draft.title}</h2>
                      <p className={styles.draftMeta}>
                        <span>Subject: {draft.subject?.title || "Unknown subject"}</span>
                        <span>Updated: {formatDate(draft.updatedAt)}</span>
                        <span>Code: {draft.publicId}</span>
                      </p>
                    </div>
                    <span className={styles.badge}>Draft</span>
                  </div>

                  <p className={styles.previewText}>{getPreview(draft)}</p>

                  <div className={styles.actionRow}>
                    <Link className={styles.primaryAction} to={workspaceUrl}>
                      Continue writing
                    </Link>
                    <Link className={styles.secondaryAction} to={buildDraftTreeUrl(draft)}>
                      Open draft tree
                    </Link>
                    <Link className={styles.secondaryAction} to={readWorkspaceUrl}>
                      Open read view
                    </Link>
                    <button
                      className={styles.secondaryAction}
                      type="button"
                      onClick={() => handleCopyWorkspaceLink(draft)}
                    >
                      {copiedDraftId === draft.id ? "Copied" : "Copy workspace link"}
                    </button>
                    <button
                      className={styles.dangerAction}
                      type="button"
                      disabled={!draft.canPublish || publishingDraftId === draft.id}
                      onClick={() => handlePublish(draft)}
                      title={draft.canPublish ? "Publish this draft" : draft.lockReasons.join(", ")}
                    >
                      {publishingDraftId === draft.id ? "Publishing…" : "Publish"}
                    </button>
                  </div>

                  <p className={styles.feedbackText}>
                    Future public URL after publish: <code>{futurePublicUrl}</code>
                  </p>
                  {publishError && <p className={styles.feedbackText}>{publishError}</p>}
                </article>
              );
            })}
          </div>
        )}
      </section>
    </main>
  );
};

export default DraftsCenterPage;
